import type { QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'

import { orgQueries } from '@/features/orgs/queries'
import { ApiError, ORG_SUSPENDED, refreshRefusedAsSuspended, scopeStore } from '@/lib/api/client'

import { returnTo } from './login-search'
import { openedOrgs } from './opened-orgs'
import { authQueries } from './queries'
import { enterOrg, enterPlatform, hasSession, resolveScope, signOut } from './session'

/**
 * The signed-in layout's `beforeLoad`: runs before any screen under it loads.
 *
 * No session, after asking the API to refresh a stale token first, sends the visitor to sign in
 * with the address they wanted. Otherwise the profile is put in the cache and the scope settled
 * (`resolveScope`): the last one while it is still theirs (an org the operator opened in this
 * browser included), else the platform for an operator with no org, else their first org, whose org is put in the cache too, so every screen renders with
 * it. A 401 that survives the client's one refresh is a session that is over: the member is
 * signed out and told it expired. A refresh or a request refused because the member's org is
 * suspended (`org_suspended`) sends them to sign in told that instead (story 20).
 */
export async function requireSession(queryClient: QueryClient, href: string): Promise<void> {
  if (!(await hasSession())) {
    const suspended = refreshRefusedAsSuspended()
    throw redirect({
      to: '/login',
      search: { redirect: returnTo(href), ...(suspended ? { reason: 'suspended' as const } : {}) },
    })
  }
  try {
    const me = await queryClient.ensureQueryData(authQueries.me())
    const opened = openedOrgs.list(me.user.id).map((org) => org.id)
    const scope = resolveScope(me, scopeStore.get(), opened)
    if (scope?.kind === 'org') await queryClient.ensureQueryData(orgQueries.current(scope.orgId))
    if (scope !== scopeStore.get()) scopeStore.set(scope)
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      signOut(queryClient, 'expired')
      throw redirect({ to: '/login', search: { redirect: returnTo(href), reason: 'expired' } })
    }
    if (error instanceof ApiError && error.code === ORG_SUSPENDED) {
      signOut(queryClient, 'suspended')
      throw redirect({ to: '/login', search: { redirect: returnTo(href), reason: 'suspended' } })
    }
    throw error
  }
}

/**
 * The org app's `beforeLoad` (`_app/_org`): its screens work in an org. Come from the console,
 * the operator goes to their first org; an operator who belongs to none goes back to the
 * console. Someone in no org at all is left to the signed-in layout, which says so.
 */
export async function requireOrgScope(queryClient: QueryClient): Promise<void> {
  if (scopeStore.get()?.kind === 'org') return
  const me = await queryClient.ensureQueryData(authQueries.me())
  const first = me.memberships[0]
  if (first !== undefined) return enterOrg(queryClient, first.org_id)
  if (me.user.is_operator) throw redirect({ to: '/platform/overview', replace: true })
}

/**
 * The platform console's `beforeLoad` (`_app/platform`): the console is the platform
 * operator's alone (ADR 0003). Anyone else is sent to the org app; the operator works across the
 * platform while in it.
 */
export async function requirePlatformScope(queryClient: QueryClient): Promise<void> {
  const me = await queryClient.ensureQueryData(authQueries.me())
  if (!me.user.is_operator) throw redirect({ to: '/evaluations', replace: true })
  if (scopeStore.get()?.kind !== 'platform') enterPlatform()
}

/**
 * The `beforeLoad` of an address the org app used to have (`/market`, `/watches`,
 * `/source-health`): the platform operator goes to the console's screen, the redirect
 * `toConsole` makes, and anyone else to the evaluations. Always throws a redirect.
 */
export async function movedToConsole(
  queryClient: QueryClient,
  toConsole: () => ReturnType<typeof redirect>,
): Promise<never> {
  if ((await queryClient.ensureQueryData(authQueries.me())).user.is_operator) throw toConsole()
  throw redirect({ to: '/evaluations', replace: true })
}
